import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { client } from '@/lib/api';
import { ChevronDown, ChevronRight, Loader2, AlertCircle, MessageSquare, User } from 'lucide-react';

type AuditCategory = 'all' | 'api_read' | 'api_write' | 'browser' | 'plan' | 'error';

interface AuditEntry {
  id: string;
  type: string;
  category: string;
  method?: string;
  endpoint?: string;
  status?: number;
  latencyMs?: number;
  description: string;
  userId?: string;
  userEmail?: string;
  conversationId?: string;
  conversationTitle?: string;
  createdAt: string;
}

const CATEGORIES: { value: AuditCategory; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'api_read', label: 'API Reads' },
  { value: 'api_write', label: 'API Writes' },
  { value: 'browser', label: 'Browser' },
  { value: 'plan', label: 'Plans' },
  { value: 'error', label: 'Errors' },
];

const CATEGORY_COLORS: Record<string, string> = {
  api_read: 'bg-blue-500/15 text-blue-400',
  api_write: 'bg-amber-500/15 text-amber-400',
  browser: 'bg-purple-500/15 text-purple-400',
  plan: 'bg-emerald-500/15 text-emerald-400',
  error: 'bg-red-500/15 text-red-400',
};

const CATEGORY_LABELS: Record<string, string> = {
  api_read: 'READ',
  api_write: 'WRITE',
  browser: 'BROWSER',
  plan: 'PLAN',
  error: 'ERROR',
};

function StatusBadge({ status }: { status?: number }) {
  if (!status) return null;
  const color = status >= 200 && status < 300
    ? 'text-emerald-400'
    : status >= 400
      ? 'text-red-400'
      : 'text-amber-400';
  return <span className={`font-mono text-xs ${color}`}>{status}</span>;
}

function LatencyText({ ms }: { ms?: number }) {
  if (ms === undefined || ms === null) return null;
  const color = ms < 500 ? 'text-muted-foreground' : ms < 2000 ? 'text-amber-400' : 'text-red-400';
  return <span className={`font-mono text-xs ${color}`}>{ms}ms</span>;
}

function TimeAgo({ iso }: { iso: string }) {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  let text: string;
  if (diffSec < 60) text = `${diffSec}s ago`;
  else if (diffMin < 60) text = `${diffMin}m ago`;
  else if (diffHr < 24) text = `${diffHr}h ago`;
  else text = `${diffDay}d ago`;

  return (
    <span className="text-xs text-muted-foreground" title={date.toLocaleString()}>
      {text}
    </span>
  );
}

function SourceCell({ entry }: { entry: AuditEntry }) {
  const navigate = useNavigate();

  if (entry.conversationId) {
    const label = entry.conversationTitle
      ? (entry.conversationTitle.length > 24 ? entry.conversationTitle.slice(0, 22) + '...' : entry.conversationTitle)
      : 'Chat';
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/chat?c=${entry.conversationId}`);
        }}
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline truncate max-w-[140px]"
        title={entry.conversationTitle ?? entry.conversationId}
      >
        <MessageSquare className="h-3 w-3 shrink-0" />
        <span className="truncate">{label}</span>
      </button>
    );
  }

  if (entry.userEmail) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground truncate max-w-[140px]" title={entry.userEmail}>
        <User className="h-3 w-3 shrink-0" />
        <span className="truncate">{entry.userEmail}</span>
      </span>
    );
  }

  return <span className="text-xs text-muted-foreground/50">System</span>;
}

function EntryRow({ entry }: { entry: AuditEntry }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className="border-b border-border/50 hover:bg-secondary/30 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-4 py-3 whitespace-nowrap">
          <TimeAgo iso={entry.createdAt} />
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold tracking-wider ${CATEGORY_COLORS[entry.category] ?? 'bg-secondary text-muted-foreground'}`}>
            {CATEGORY_LABELS[entry.category] ?? entry.category.toUpperCase()}
          </span>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {entry.method && (
              <span className="font-mono text-xs font-medium text-foreground w-12 shrink-0">
                {entry.method}
              </span>
            )}
            <span className="text-sm text-muted-foreground truncate max-w-[300px]" title={entry.endpoint ?? entry.description}>
              {entry.endpoint ?? entry.description}
            </span>
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <StatusBadge status={entry.status} />
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <LatencyText ms={entry.latencyMs} />
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <SourceCell entry={entry} />
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-border/50">
          <td colSpan={7} className="px-4 py-3 bg-secondary/20">
            <div className="space-y-1 text-xs">
              <div><span className="text-muted-foreground">Event type:</span> <span className="font-mono text-foreground">{entry.type}</span></div>
              <div><span className="text-muted-foreground">Description:</span> <span className="text-foreground">{entry.description}</span></div>
              {entry.userEmail && (
                <div><span className="text-muted-foreground">User:</span> <span className="text-foreground">{entry.userEmail}</span>{entry.userId && <span className="text-muted-foreground/60 ml-1">({entry.userId})</span>}</div>
              )}
              {entry.conversationTitle && (
                <div><span className="text-muted-foreground">Conversation:</span> <span className="text-foreground">{entry.conversationTitle}</span>{entry.conversationId && <span className="text-muted-foreground/60 ml-1">({entry.conversationId})</span>}</div>
              )}
              {entry.conversationId && !entry.conversationTitle && (
                <div><span className="text-muted-foreground">Conversation ID:</span> <span className="font-mono text-foreground">{entry.conversationId}</span></div>
              )}
              <div><span className="text-muted-foreground">Time:</span> <span className="text-foreground">{new Date(entry.createdAt).toLocaleString()}</span></div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function AuditLogPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<AuditCategory>('all');

  const fetchEntries = useCallback(async (cat: AuditCategory, offset: number, append: boolean) => {
    try {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);

      const res = await client.database.getAuditLog({
        limit: 50,
        offset,
        category: cat,
      });

      if (append) {
        setEntries(prev => [...prev, ...res.entries]);
      } else {
        setEntries(res.entries);
      }
      setTotal(res.total);
      setHasMore(res.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit log');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries(category, 0, false);
  }, [category, fetchEntries]);

  const handleLoadMore = () => {
    fetchEntries(category, entries.length, true);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b border-border px-6 py-4 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Audit Log</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Zoom API calls and browser automation actions
            </p>
          </div>
          {!loading && (
            <span className="text-xs text-muted-foreground">
              {total.toLocaleString()} event{total !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Category filters */}
        <div className="flex items-center gap-2 mt-4">
          {CATEGORIES.map(cat => (
            <button
              key={cat.value}
              onClick={() => setCategory(cat.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                category === cat.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary/50 text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <AlertCircle className="h-8 w-8 text-red-400" />
            <p className="text-sm text-muted-foreground">{error}</p>
            <button
              onClick={() => fetchEntries(category, 0, false)}
              className="text-xs text-primary hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && entries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-2">
            <p className="text-sm text-muted-foreground">No activity found</p>
            <p className="text-xs text-muted-foreground/60">
              Zoom API calls and browser actions will appear here
            </p>
          </div>
        )}

        {!loading && !error && entries.length > 0 && (
          <>
            <table className="w-full">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-4 py-2 text-xs font-medium text-muted-foreground w-24">Time</th>
                  <th className="px-4 py-2 text-xs font-medium text-muted-foreground w-24">Type</th>
                  <th className="px-4 py-2 text-xs font-medium text-muted-foreground">Endpoint</th>
                  <th className="px-4 py-2 text-xs font-medium text-muted-foreground w-16">Status</th>
                  <th className="px-4 py-2 text-xs font-medium text-muted-foreground w-20">Latency</th>
                  <th className="px-4 py-2 text-xs font-medium text-muted-foreground w-36">Source</th>
                  <th className="px-4 py-2 w-8" />
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <EntryRow key={entry.id} entry={entry} />
                ))}
              </tbody>
            </table>

            {hasMore && (
              <div className="flex justify-center py-4">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-secondary/50 hover:bg-secondary rounded-lg transition-colors disabled:opacity-50"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load more'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
